import Card from "components/card";
import { FactoryFormScheme } from "./scheme";
import { FormProvider, useForm } from "react-hook-form";
import InputField from "components/fields/InputField";
import { Button } from "components/Button";
import { useFactoryContract } from "hooks/useFactoryContract";
import { isAddress, parseUnits } from "ethers";
import { useAccount } from "wagmi";
import { usePoolProvider } from "context";
import { useEffect } from "react";
import { useNetworksStore } from "store/networksStore";
import { Loader } from "components/Loader";
import { shortenAddress } from "utils/shortenAddress";
import { polygonId } from "../../../constants";
import { Fee } from "../../../types/fee";
import { FeeComponent } from "./components/FeeComponent";

export const FactorySettings = () => {
  const { address: account } = useAccount();
  const { jsonRpcProvider } = usePoolProvider();
  const { currentNetwork } = useNetworksStore();
  const currencyDecimals = currentNetwork.depositTokens[0].decimals;

  const defaultFee = {
    from: "",
    percentage: "",
    fixedFee: "",
  };

  const {
    xbrFeeValue,
    getFactoryState,
    poolCreationFeeValue,
    factoryOwner,
    setXBRDiscountNumerator,
    setPoolCreationFee,
    sweepToken,
    isLoadingState,
    setFeesInfo,
  } = useFactoryContract(jsonRpcProvider);

  const form = useForm<FactoryFormScheme>({
    values: {
      creationFee: "",
      stakingFeeFlex: Array.from({ length: 5 }, () => ({ ...defaultFee })),
      unstakingFeeFlex: Array.from({ length: 5 }, () => ({ ...defaultFee })),
      xbrDiscount: "",
      address: "",
      submiting: false,
    },
  });

  const xbrDiscount = form.watch("xbrDiscount");
  const creationFee = form.watch("creationFee");
  const address = form.watch("address");
  const submiting = form.watch("submiting");
  const stakingFeeFlex = form.watch("stakingFeeFlex");
  const unstakingFeeFlex = form.watch("unstakingFeeFlex");

  const getFactorySettings = async () => {
    try {
      const state = await getFactoryState();
      const [stakingFeeFlex, unstakingFeeFlex] = state;

      if (stakingFeeFlex?.length) {
        form.setValue("stakingFeeFlex", stakingFeeFlex);
      }

      if (unstakingFeeFlex?.length) {
        form.setValue("unstakingFeeFlex", unstakingFeeFlex);
      }
    } catch (error) {
      console.error("Error fetching factory settings:", error);
    }
  };

  useEffect(() => {
    getFactorySettings();
  }, [jsonRpcProvider]);

  const handleSetXBRDiscounteNumerator = async (xbrDiscount: string) => {
    if (
      !xbrDiscount ||
      !parseFloat(xbrDiscount) ||
      parseFloat(xbrDiscount) > 100
    )
      return;

    try {
      form.setValue("submiting", true);
      await setXBRDiscountNumerator(parseFloat(xbrDiscount) * 100);

      form.setValue("xbrDiscount", "");

      form.setValue("submiting", false);
      await getFactorySettings();
    } catch (error) {
      console.error(error);
      form.setValue("submiting", false);
    }
  };

  const handleSetPoolCreationFee = async (creationFee: string) => {
    if (!creationFee || !parseFloat(creationFee)) return;

    try {
      form.setValue("submiting", true);
      await setPoolCreationFee(parseUnits(creationFee, currencyDecimals));
      await getFactoryState();

      form.setValue("submiting", false);
    } catch (error) {
      console.error(error);
      form.setValue("submiting", false);
    }
  };

  const handleSweepToken = async (address: string) => {
    if (!isAddress(address)) return;

    try {
      form.setValue("submiting", true);
      await sweepToken(address);
      await getFactoryState();

      form.setValue("address", "");

      form.setValue("submiting", false);
    } catch (error) {
      console.error(error);
      form.setValue("submiting", false);
    }
  };

  const handleChangeFees = async ({
    fees,
    isDeposit,
  }: {
    fees: Fee[];
    isDeposit: boolean;
  }) => {
    try {
      form.setValue("submiting", true);
      await setFeesInfo({ fees, isDeposit });
      await getFactorySettings();
      form.setValue("submiting", false);
    } catch (error) {
      console.error(error);
      form.setValue("submiting", false);
    }
  };

  const handleFeeChange = (
    index: number,
    field: keyof Fee,
    newValue: string,
    feeType: "stakingFeeFlex" | "unstakingFeeFlex"
  ) => {
    if (/^\d*$/.test(newValue)) {
      const currentFees = form.getValues(feeType);
      const updatedFees = currentFees.map((fee, i) =>
        i === index ? { ...fee, [field]: newValue } : fee
      );
      form.setValue(feeType, updatedFees);
    }
  };

  const handleFeeRemove = (
    index: number,
    feeType: "stakingFeeFlex" | "unstakingFeeFlex"
  ) => {
    const currentFees = form.getValues(feeType);
    const updatedFees = currentFees.filter((_, i) => i !== index);
    form.setValue(feeType, updatedFees);
  };

  const handleAddFeeRecord = (
    feeType: "stakingFeeFlex" | "unstakingFeeFlex"
  ) => {
    const currentFees = form.getValues(feeType);
    const newFee: Fee = {
      from: "",
      // to: "",
      percentage: "",
      fixedFee: "",
    };
    form.setValue(feeType, [...currentFees, newFee]);
  };

  return (
    <form>
      <div className="text-mauve12 mt-3 flex flex-col gap-6">
        <Card>
          <div className="p-4">
            <h2 className="text-mauve12 mb-4 text-[17px] font-medium">
              Creation Factory values
            </h2>
            {isLoadingState ? (
              <Loader />
            ) : (
              <>
                <div className="mb-2 flex gap-3 rounded-md bg-gray-100 p-2">
                  <div>
                    <span>Owner address: {shortenAddress(factoryOwner)}</span>
                  </div>
                  <div>
                    <span>
                      Factory address:{" "}
                      {shortenAddress(currentNetwork.factoryContractAddress)}
                    </span>
                  </div>
                </div>

                <ul className="flex gap-4 [&>li]:flex [&>li]:gap-2 [&>li]:rounded-md [&>li]:bg-gray-200 [&>li]:p-2">
                  <li>
                    <span>Pool Creation Fee:</span>
                    <span>{poolCreationFeeValue}$</span>
                  </li>
                  {currentNetwork.chainId === polygonId && (
                    <li>
                      <span>XBR Discount:</span>
                      <span>{xbrFeeValue}%</span>
                    </li>
                  )}
                </ul>
              </>
            )}
          </div>
        </Card>

        {factoryOwner &&
          !isLoadingState &&
          account &&
          factoryOwner.toLowerCase() === account.toLowerCase() && (
            <>
              <Card>
                <div className="p-4">
                  <h2 className="text-mauve12 mb-4 text-[17px] font-medium">
                    Change Pool Creation Fee
                  </h2>
                  <FormProvider {...form}>
                    <fieldset className="mb-[15px] flex-col">
                      <div className="flex items-end gap-5">
                        <InputField
                          variant="auth"
                          state={form.formState.errors.creationFee && "error"}
                          extra="w-[320px]"
                          label="Pool Creation Fee ($):"
                          placeholder="0"
                          id="creationFee"
                          type="text"
                          {...form.register("creationFee", {})}
                        />

                        <Button
                          className="flex h-12 w-[100px] items-center justify-center"
                          disabled={submiting}
                          onClick={() => handleSetPoolCreationFee(creationFee)}
                        >
                          {submiting ? "Loading..." : "Change"}
                        </Button>
                      </div>
                      {form.formState.errors.creationFee && (
                        <span className="text-sm text-red-500 dark:text-red-400">
                          {form.formState.errors.creationFee.message}
                        </span>
                      )}
                    </fieldset>

                    {currentNetwork.chainId === polygonId && (
                      <fieldset className="mb-[15px] flex-col">
                        <div className="flex items-end gap-5">
                          <InputField
                            variant="auth"
                            state={form.formState.errors.xbrDiscount && "error"}
                            extra="w-[320px]"
                            label="XBR Discount (%):"
                            placeholder="0"
                            id="xbrDiscount"
                            type="text"
                            {...form.register("xbrDiscount", {})}
                          />

                          <Button
                            className="flex h-12 w-[100px] items-center justify-center"
                            disabled={submiting}
                            onClick={() =>
                              handleSetXBRDiscounteNumerator(xbrDiscount)
                            }
                          >
                            {submiting ? "Loading..." : "Change"}
                          </Button>
                        </div>
                        {form.formState.errors.xbrDiscount && (
                          <span className="text-sm text-red-500 dark:text-red-400">
                            {form.formState.errors.xbrDiscount.message}
                          </span>
                        )}
                      </fieldset>
                    )}
                  </FormProvider>
                </div>
              </Card>

              <Card>
                <div className="p-4">
                  <h2 className="text-mauve12 mb-4 text-[17px] font-medium">
                    Change factory Staking fees
                  </h2>

                  {stakingFeeFlex.map((fee, index) => (
                    <FeeComponent
                      key={index}
                      fee={fee}
                      index={index}
                      onChange={(...params) =>
                        handleFeeChange(...params, "stakingFeeFlex")
                      }
                      onRemove={(index) =>
                        handleFeeRemove(index, "stakingFeeFlex")
                      }
                      disabled={submiting}
                    />
                  ))}

                  <div className="mt-4 flex gap-6">
                    <Button
                      className="flex h-12 w-[100px] items-center justify-center"
                      disabled={submiting}
                      onClick={() =>
                        handleChangeFees({
                          fees: stakingFeeFlex,
                          isDeposit: true,
                        })
                      }
                    >
                      {submiting ? "Loading..." : "Change"}
                    </Button>
                    <Button
                      className="flex h-12 w-fit items-center justify-center"
                      disabled={submiting}
                      onClick={() => handleAddFeeRecord("stakingFeeFlex")}
                    >
                      Add new
                    </Button>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="p-4">
                  <h2 className="text-mauve12 mb-4 text-[17px] font-medium">
                    Change factory Unstaking fees
                  </h2>

                  {unstakingFeeFlex.map((fee, index) => (
                    <FeeComponent
                      key={index}
                      fee={fee}
                      index={index}
                      onChange={(...params) =>
                        handleFeeChange(...params, "unstakingFeeFlex")
                      }
                      onRemove={(index) =>
                        handleFeeRemove(index, "unstakingFeeFlex")
                      }
                      disabled={submiting}
                    />
                  ))}

                  <div className="mt-4 flex gap-6">
                    <Button
                      className="flex h-12 w-[100px] items-center justify-center"
                      disabled={submiting}
                      onClick={() =>
                        handleChangeFees({
                          fees: unstakingFeeFlex,
                          isDeposit: false,
                        })
                      }
                    >
                      {submiting ? "Loading..." : "Change"}
                    </Button>
                    <Button
                      className="flex h-12 w-fit items-center justify-center"
                      disabled={submiting}
                      onClick={() => handleAddFeeRecord("unstakingFeeFlex")}
                    >
                      Add new
                    </Button>
                  </div>
                </div>
              </Card>

              <Card>
                <div className="p-4">
                  <h2 className="text-mauve12 mb-4 text-[17px] font-medium">
                    Transfer given token from factory contract to the owner
                  </h2>
                  <fieldset className="mb-[15px] flex-col">
                    <div className="flex items-end gap-5">
                      <InputField
                        variant="auth"
                        state={form.formState.errors.creationFee && "error"}
                        extra="w-[320px]"
                        label="Address:"
                        placeholder="0x"
                        id="address"
                        type="text"
                        {...form.register("address", {})}
                      />

                      <Button
                        className="flex h-12 w-[100px] items-center justify-center"
                        disabled={submiting || !isAddress(address)}
                        onClick={() => handleSweepToken(address)}
                      >
                        {submiting ? "Loading..." : "Sweep"}
                      </Button>
                    </div>
                    {form.formState.errors.address && (
                      <span className="text-sm text-red-500 dark:text-red-400">
                        {form.formState.errors.address.message}
                      </span>
                    )}
                  </fieldset>
                </div>
              </Card>
            </>
          )}
      </div>
    </form>
  );
};
